import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../../../../components/ui/button";
import { Badge } from "../../../../../components/ui/badge";
import { Calendar } from "../../../../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../../components/ui/popover";
import { format } from "date-fns";
import { primaryImageFirst } from "../../../../../utils/imageUrl";
import {
  MapPin,
  Star,
  Wifi,
  UtensilsCrossed,
  Users,
  Heart,
  Share2,
  Coffee,
  Dumbbell,
  Waves,
  Car,
  ChevronLeft,
  ChevronRight,
  X,
  CalendarIcon,
  Minus,
  Plus,
  Clock,
  Shield,
  CreditCard,
  Phone,
  Mail,
  Bed,
  Home,
  CheckCircle2,
  Tv,
  AirVent,
  WashingMachine,
  Refrigerator,
  Utensils,
  Lock,
  Cigarette,
  PawPrint,
  Music,
  Gamepad2,
  BookOpen,
  Camera,
  Tag,
  User,
} from "lucide-react";
import { getKeywordBasedSearch, getShortTermSearch } from "../../../../../api/services/hostService";
import { postBooking, postShortTermBooking } from "../../../../../api/services/guestService";
import { IShortTermProperty, IVacantBedListing, IVacantRoomListing } from "../../../../model/guestModel";
import { useUserContext } from "../../../../protectedRoute/UserContext";
import LandingHeader from "../../../../../components/landing/Header";
import LandingPageHeader from "../../../../../components/LandingPageHeader/LandingPageHeader";
import ExpandableText from "../../../../../components/ui/ExpandableText";

declare global {
  interface Window {
    lucide: any;
  }
}

const DEFAULT_PROPERTY_IMAGE = "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?q=80&w=1200";

interface Location {
  city?: string;
  locality?: string;
  street_address?: string;
}

interface Bed {
  id?: number;
  bed_number?: string;
  description?: string;
  is_booked?: boolean;
  price_per_bed?: string;
  co_roommate_gender?: string;
    bed_category?: string; // New field
  capacity?: number;
}

interface Room {
  id?: number;
  room_number?: string;
  description?: string;
  capacity?: number;
  price_per_room?: string;
  property?: number;
}

interface Review {
  id: number;
  guest: number;
  guest_first_name: string;
  property: number;
  rating: number;
  comment: string;
}

interface PropertyData {
  id: number;
  name: string;
  beds: Bed[];
  rooms: Room[];
  location: Location;
  facilities?: string[];
  description?: string;
  images: { image: string; is_primary?: boolean }[];
  type?: string;
  food_available?: boolean;
  food_availability_details?: string[];
  highlights?: string[];
  property_rules?: string[];
  average_rating: number;
  total_reviews: number;
  reviews: Review[];
  coordinates: { lat: number; lng: number };
}

const amenityDetails: Record<string, { icon: React.ElementType; label: string; description: string }> = {
  wifi: { icon: Wifi, label: "Free WiFi", description: "High-speed internet throughout" },
  meals: { icon: UtensilsCrossed, label: "All Meals", description: "Breakfast, lunch & dinner" },
  pool: { icon: Waves, label: "Swimming Pool", description: "Outdoor pool with loungers" },
  cafe: { icon: Coffee, label: "Café", description: "On-site café & snacks" },
  gym: { icon: Dumbbell, label: "Gym", description: "Fitness center access" },
  parking: { icon: Car, label: "Free Parking", description: "Secure parking available" },
  ac: { icon: AirVent, label: "Air Conditioning", description: "Climate controlled rooms" },
  tv: { icon: Tv, label: "TV", description: "Flat-screen TV" },
  laundry: { icon: WashingMachine, label: "Laundry", description: "Laundry facilities" },
  kitchen: { icon: Utensils, label: "Kitchen", description: "Shared kitchen access" },
  locker: { icon: Lock, label: "Lockers", description: "Secure personal lockers" },
  games: { icon: Gamepad2, label: "Games Room", description: "Board games & entertainment" },
  library: { icon: BookOpen, label: "Library", description: "Book exchange available" },
};

const PropertyDetail = () => {
  const { property_id } = useParams<{ property_id: string }>();
  const [searchParams] = useSearchParams();
  const [propertyData, setPropertyData] = useState<PropertyData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRooms, setSelectedRooms] = useState<Room[]>([]);
  const [selectedBeds, setSelectedBeds] = useState<Bed[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [gstAmount, setGstAmount] = useState<number>(0);
  const [finalTotalAmount, setFinalTotalAmount] = useState<number>(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { state } = useUserContext();
  const { hostID, role } = state;
  const navigate = useNavigate();

  const keyword = searchParams.get("keyword") || "Koramangala";
  const maleGuests = Number(searchParams.get("male_guests")) || 0;
  const femaleGuests = Number(searchParams.get("female_guests")) || 0;
  const checkin = searchParams.get("checkin");
  const checkout = searchParams.get("checkout");
  const accommodation_type = searchParams.get("accommodation_type") || "Bed";

  const isShortTerm = accommodation_type === "short_bed" || accommodation_type === "short_room";

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [checkInDate, setCheckInDate] = useState<Date | undefined>(checkin ? new Date(checkin) : undefined);
  const [checkOutDate, setCheckOutDate] = useState<Date | undefined>(checkout ? new Date(checkout) : undefined);
  const [maleGuestsCount, setMaleGuestsCount] = useState(maleGuests);
  const [femaleGuestsCount, setFemaleGuestsCount] = useState(femaleGuests);
  const [bookingType, setBookingType] = useState<"bed" | "room">(
    accommodation_type === "room" ? "room" : "bed"
  );
  const [bedSelections, setBedSelections] = useState<Record<string, number[]>>({});

  // Short-term state
  const [shortTermData, setShortTermData] = useState<IShortTermProperty | null>(null);
  const [stBedQty, setStBedQty] = useState<Record<string, number>>({});
  const [stRoomQty, setStRoomQty] = useState<Record<string, number>>({});
  const [stBooking, setStBooking] = useState(false);

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        setLoading(true);

        if (isShortTerm) {
          const accType = accommodation_type as "short_bed" | "short_room";
          const results = await getShortTermSearch({ checkin: checkin || "", checkout: checkout || "", accommodation_type: accType, property_id });
          const data: any = results;
          const found: IShortTermProperty | null = Array.isArray(data)
            ? (data.find((p: IShortTermProperty) => p.id === property_id) || data[0] || null)
            : (data?.id ? data : null);
          if (found) setShortTermData({ ...found, images: primaryImageFirst(found.images) });
          else setError("Property not found.");
          return;
        }

        const payload: any = {
          keyword,
          male_guests: maleGuests,
          female_guests: femaleGuests,
          checkin,
          checkout,
          accommodation_type,
          property_id: property_id,
        };

        const response = await getKeywordBasedSearch(payload);

        if (response && typeof response === "object" && !Array.isArray(response)) {
          setPropertyData({ ...response, images: primaryImageFirst(response.images), coordinates: { lat: 12.9352, lng: 77.6245 } });
        } else if (Array.isArray(response)) {
          if (response.length === 1) {
            setPropertyData({ ...response[0], images: primaryImageFirst(response[0].images), coordinates: { lat: 12.9352, lng: 77.6245 } });
          }
        } else {
          setError("Invalid response received from API.");
        }
      } catch (error) {
        setError("Failed to fetch property details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyData();
  }, [property_id, keyword, maleGuests, femaleGuests, checkin, checkout, accommodation_type, searchParams]);

  const getTotalBeds = () => Object.values(bedSelections).reduce((sum, beds) => sum + beds.length, 0);

  const getTotalBedPrice = () => {
    return Object.values(bedSelections).flat().reduce((sum, bedId) => {
      const bed = propertyData?.beds.find(b => b.id === bedId);
      return sum + (parseFloat(bed?.price_per_bed || "0"));
    }, 0);
  };

  const updateBedSelection = (category: string, delta: number) => {
    setBedSelections(prev => {
      const currentBeds = prev[category] || [];
      if (delta > 0) {
        const availableBed = propertyData?.beds.find(b => b.bed_category === category && !b.is_booked && !currentBeds.includes(b.id!));
        if (availableBed) {
          return { ...prev, [category]: [...currentBeds, availableBed.id!] };
        }
      } else if (delta < 0 && currentBeds.length > 0) {
        const newBeds = [...currentBeds];
        newBeds.pop();
        if (newBeds.length === 0) {
          const { [category]: removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [category]: newBeds };
      }
      return prev;
    });
  };

  const nextImage = () => {
    if (propertyData) {
      setCurrentImageIndex((prev) => (prev + 1) % propertyData!.images.length);
    }
  };

  const prevImage = () => {
    if (propertyData) {
      setCurrentImageIndex((prev) => (prev - 1 + propertyData!.images.length) % propertyData!.images.length);
    }
  };

  const calculateTotal = () => {
    if (!checkInDate || !checkOutDate) return 0;
    const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
    const basePrice = bookingType === "bed" ? getTotalBedPrice() : (propertyData?.rooms[0]?.price_per_room ? parseFloat(propertyData!.rooms[0].price_per_room) : 0);
    return nights * basePrice;
  };

  const nights = checkInDate && checkOutDate
    ? Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  const PLATFORM_FEE_RATE = 0.10;
  const GST_RATE = 0.18;

  // Short-term price totals
  const stNightlyTotal = Object.entries(stBedQty).reduce((sum, [id, qty]) => {
    const bed = shortTermData?.vacant_beds.find(b => b.id === id);
    return sum + (parseFloat(bed?.price_per_bed || "0") * qty);
  }, 0) + Object.entries(stRoomQty).reduce((sum, [id, qty]) => {
    const room = shortTermData?.vacant_rooms.find(r => r.id === id);
    return sum + (parseFloat(room?.price_per_room || "0") * qty);
  }, 0);
  const stGrandTotal = stNightlyTotal * nights;
  const stReserve = Math.round(stGrandTotal * PLATFORM_FEE_RATE);
  const stGst = Math.round(stReserve * GST_RATE);
  const stPayNow = stReserve + stGst;

  const handleShortTermBookNow = async () => {
    if (!checkInDate || !checkOutDate || stGrandTotal <= 0) return;
    const items = [
      ...Object.entries(stBedQty).filter(([, q]) => q > 0).map(([id, qty]) => ({ item_type: "vacant_bed" as const, item_id: id, quantity: qty })),
      ...Object.entries(stRoomQty).filter(([, q]) => q > 0).map(([id, qty]) => ({ item_type: "vacant_room" as const, item_id: id, quantity: qty })),
    ];
    if (items.length === 0) return;
    setStBooking(true);
    try {
      const res = await postShortTermBooking({ items, check_in: format(checkInDate, "yyyy-MM-dd"), check_out: format(checkOutDate, "yyyy-MM-dd") });
      if (res?.booking_id) navigate(`/booking-confirmation/${res.booking_id}`);
    } catch (e: any) {
      alert(e.message || "Booking failed. Please try again.");
    } finally {
      setStBooking(false);
    }
  };

  const handleBookNow = async () => {
    try {
      const baseTotal = calculateTotal();
      const reserve = Math.round(baseTotal * PLATFORM_FEE_RATE);
      const gst = Math.round(reserve * GST_RATE);
      const payNow = reserve + gst;
      const payload: any = {
        items: [
          ...selectedRooms.map((room) => ({ item_type: "room", item_id: room.id })),
          ...Object.values(bedSelections).flat().map(bedId => ({ item_type: "bed", item_id: bedId }))
        ],
        check_in: checkin,
        check_out: checkout,
        total_price: baseTotal,
        gst_amount: gst,
        price_with_gst: payNow,
      };
      const response = await postBooking(payload);
      if (response.booking_id) {
        navigate(`/booking-confirmation/${response?.booking_id}`);
      }
    } catch (error: any) {
      console.error("Failed to create booking:", error.message || error);
      alert("Failed to create booking. Please try again.");
    }
  };
  
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://unpkg.com/lucide@latest";
    script.onload = () => {
      if (window.lucide && typeof window.lucide.createIcons === 'function') {
        window.lucide.createIcons();
      }
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-deep-emerald-green"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center mt-8 text-xl">{error}</div>;
  }

  if (!propertyData && !isShortTerm) {
    return <div className="text-center mt-8 text-xl">No property data found.</div>;
  }

  if (isShortTerm && !shortTermData && !loading) {
    return <div className="text-center mt-8 text-xl">No property data found.</div>;
  }

  // ── Short-term render ──────────────────────────────────────────────────────
  if (isShortTerm && shortTermData) {
    const p = shortTermData;
    const images = p.images || [];
    const facilities: string[] = Array.isArray(p.facilities) ? p.facilities : [];
    const highlights: string[] = Array.isArray(p.highlights) ? p.highlights : [];
    const propertyRules: string[] = Array.isArray(p.property_rules) ? p.property_rules : [];
    const backUrl = `/guest-search-results?keyword=${keyword}&checkin=${checkin || ""}&checkout=${checkout || ""}&accommodation_type=${accommodation_type}`;

    const stFromPrice = [
      ...p.vacant_beds.map(b => parseFloat(b.price_per_bed)),
      ...p.vacant_rooms.map(r => parseFloat(r.price_per_room)),
    ].filter(v => !isNaN(v));

    return (
      <div className="bg-background">
        {role === 'guest' ? <LandingHeader /> : <LandingPageHeader isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />}

        <main className="pt-20">
          <section className="relative">
            <div className="container mx-auto px-4 py-6">
              <div className="flex items-center justify-between mb-4">
                <Link to={backUrl} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                  <ChevronLeft size={20} />
                  <span>Back to search</span>
                </Link>
                <Badge className="bg-primary/10 text-primary border-primary/20">Short-term Stay</Badge>
              </div>

              {images.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-2 rounded-xl overflow-hidden md:h-[420px]">
                    <div className="md:col-span-2 md:row-span-2 relative aspect-[4/3] overflow-hidden cursor-pointer group" onClick={() => { setCurrentImageIndex(0); setIsGalleryOpen(true); }}>
                      <img src={images[0]?.image} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = DEFAULT_PROPERTY_IMAGE; }} />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                    </div>
                    {images.slice(1, 5).map((image, index) => (
                      <div key={index} className="hidden md:block relative aspect-[4/3] overflow-hidden cursor-pointer group" onClick={() => { setCurrentImageIndex(index + 1); setIsGalleryOpen(true); }}>
                        <img src={image.image} alt={`${p.name} ${index + 2}`} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = DEFAULT_PROPERTY_IMAGE; }} />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                        {index === 3 && images.length > 5 && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <div className="flex items-center gap-2 text-white"><Camera size={20} /><span className="font-medium">+{images.length - 5} more</span></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" className="mt-4 md:hidden" onClick={() => { setCurrentImageIndex(0); setIsGalleryOpen(true); }}>
                    <Camera size={16} className="mr-2" />View all {images.length} photos
                  </Button>
                </>
              ) : (
                <div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-muted">
                  <img src={DEFAULT_PROPERTY_IMAGE} alt="No Image Available" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </section>

          <section className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left col */}
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h1 className="text-2xl md:text-3xl font-bold text-foreground">{p.name}</h1>
                      <div className="flex items-center gap-2 mt-2 text-muted-foreground">
                        <MapPin size={16} />
                        <span>{[p.location.street_address, p.location.locality, p.location.city].filter(Boolean).join(", ")}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 bg-primary/10 px-3 py-1 rounded-full flex-shrink-0">
                      <Star size={18} className="fill-primary text-primary" />
                      <span className="font-semibold text-foreground">{Number(p.average_rating).toFixed(1)}</span>
                      <span className="text-muted-foreground">({p.total_reviews})</span>
                    </div>
                  </div>
                  {p.property_type && (
                    <div className="flex flex-wrap items-center gap-4 py-4 border-y border-border">
                      <div className="flex items-center gap-2"><Bed size={20} className="text-muted-foreground" /><span>{p.property_type}</span></div>
                    </div>
                  )}
                </div>

                {p.description && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-4">About this place</h2>
                    <ExpandableText text={p.description} className="text-muted-foreground leading-relaxed" />
                  </div>
                )}

                {facilities.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-4">Amenities</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {facilities.map((amenity) => {
                        const detail = amenityDetails[amenity.toLowerCase()];
                        if (!detail) return null;
                        const Icon = detail.icon;
                        return (
                          <div key={amenity} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                            <Icon size={24} className="text-primary flex-shrink-0" />
                            <div>
                              <p className="font-medium text-foreground">{detail.label}</p>
                              <p className="text-sm text-muted-foreground">{detail.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {highlights.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-4">Highlights</h2>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {highlights.map((h, i) => (
                        <li key={i} className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle2 size={18} className="text-primary" /><span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {propertyRules.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-4">Property Rules</h2>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {propertyRules.map((rule, i) => (
                        <li key={i} className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle2 size={18} className="text-primary" /><span>{rule}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {p.food_available && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-4">Food Availability</h2>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <UtensilsCrossed size={18} className="text-primary" />
                      <span>Food is available at this property (Breakfast, Lunch, Dinner).</span>
                    </div>
                  </div>
                )}

                {/* Vacant Beds */}
                {p.vacant_beds.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-4">Select Beds by Sharing Type</h2>
                    <div className="space-y-3">
                      {p.vacant_beds.map((bed: IVacantBedListing) => {
                        const qty = stBedQty[bed.id] || 0;
                        return (
                          <div key={bed.id} className={`p-4 rounded-lg border transition-all ${qty > 0 ? "border-primary bg-primary/5" : "border-border"}`}>
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="secondary" className="flex items-center gap-1"><Tag size={14} />{bed.bed_category}</Badge>
                                </div>
                                {bed.description && <p className="font-medium text-foreground">{bed.description}</p>}
                                <div className="flex items-center gap-4 mt-2 text-muted-foreground text-sm">
                                  <span className="flex items-center gap-1"><Users size={16} />{bed.available_beds ?? bed.vacant_beds} beds available</span>
                                  <span className="flex items-center gap-1"><CreditCard size={16} /><span className="text-primary font-bold">₹{bed.price_per_bed}</span> / night</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <Button variant="outline" size="icon" className="h-8 w-8 rounded-full"
                                  onClick={() => setStBedQty(prev => { const n = Math.max(0, (prev[bed.id] || 0) - 1); if (n === 0) { const { [bed.id]: _, ...rest } = prev; return rest; } return { ...prev, [bed.id]: n }; })}
                                  disabled={qty === 0}><Minus size={14} /></Button>
                                <span className="w-8 text-center font-medium">{qty}</span>
                                <Button variant="outline" size="icon" className="h-8 w-8 rounded-full"
                                  onClick={() => setStBedQty(prev => ({ ...prev, [bed.id]: (prev[bed.id] || 0) + 1 }))}
                                  disabled={qty >= (bed.available_beds ?? bed.vacant_beds)}><Plus size={14} /></Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Vacant Rooms */}
                {p.vacant_rooms.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-4">Select Private Rooms</h2>
                    <div className="space-y-3">
                      {p.vacant_rooms.map((room: IVacantRoomListing) => {
                        const qty = stRoomQty[room.id] || 0;
                        return (
                          <div key={room.id} className={`p-4 rounded-lg border transition-all ${qty > 0 ? "border-primary bg-primary/5" : "border-border"}`}>
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="secondary" className="flex items-center gap-1"><Tag size={14} />{room.room_category}</Badge>
                                  <Badge variant="outline">{room.room_capacity} person{room.room_capacity !== 1 ? "s" : ""}</Badge>
                                </div>
                                {room.description && <p className="font-medium text-foreground">{room.description}</p>}
                                <div className="flex items-center gap-4 mt-2 text-muted-foreground text-sm">
                                  <span className="flex items-center gap-1"><Users size={16} />{room.available_rooms ?? room.vacant_rooms} rooms available</span>
                                  <span className="flex items-center gap-1"><CreditCard size={16} /><span className="text-primary font-bold">₹{room.price_per_room}</span> / night</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <Button variant="outline" size="icon" className="h-8 w-8 rounded-full"
                                  onClick={() => setStRoomQty(prev => { const n = Math.max(0, (prev[room.id] || 0) - 1); if (n === 0) { const { [room.id]: _, ...rest } = prev; return rest; } return { ...prev, [room.id]: n }; })}
                                  disabled={qty === 0}><Minus size={14} /></Button>
                                <span className="w-8 text-center font-medium">{qty}</span>
                                <Button variant="outline" size="icon" className="h-8 w-8 rounded-full"
                                  onClick={() => setStRoomQty(prev => ({ ...prev, [room.id]: (prev[room.id] || 0) + 1 }))}
                                  disabled={qty >= (room.available_rooms ?? room.vacant_rooms)}><Plus size={14} /></Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Map */}
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-4">Location</h2>
                  <div className="relative rounded-xl overflow-hidden bg-muted aspect-[16/9]">
                    <iframe
                      title="Property Location"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=77.6145%2C12.9252%2C77.6345%2C12.9452&layer=mapnik&marker=12.9352%2C77.6245`}
                    />
                  </div>
                  <p className="mt-3 text-muted-foreground flex items-center gap-2">
                    <MapPin size={16} />
                    {[p.location.street_address, p.location.locality, p.location.city].filter(Boolean).join(", ")}
                  </p>
                </div>

                {/* Reviews */}
                {p.reviews && p.reviews.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-4">Reviews ({p.total_reviews})</h2>
                    <div className="space-y-6">
                      {p.reviews.map((review, rIdx) => {
                        const colors = ['#6d28d9','#0d9488','#ea580c','#db2777','#2563eb','#16a34a'];
                        const guestName = review.guest_first_name || 'Guest';
                        return (
                        <motion.div
                          key={review.id}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          className="border-b border-border pb-6 last:border-0"
                        >
                          <div className="flex items-start gap-4">
                            <div
                              className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-white font-bold text-lg"
                              style={{ backgroundColor: colors[rIdx % colors.length] }}
                            >
                              {guestName.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <p className="font-medium text-foreground">{guestName}</p>
                                <div className="flex items-center gap-1">
                                  {[...Array(5)].map((_, i) => (
                                    <Star key={i} size={14} className={i < review.rating ? "fill-primary text-primary" : "text-muted-foreground"} />
                                  ))}
                                </div>
                              </div>
                              <p className="text-muted-foreground">{review.comment}</p>
                            </div>
                          </div>
                        </motion.div>
                        );
                      })}
                    </div>
                    <Button variant="outline" className="w-full mt-6">
                      View all {p.total_reviews} reviews
                    </Button>
                  </div>
                )}
              </div>

              {/* Booking panel */}
              <div className="lg:col-span-1">
                <div className="p-6 rounded-xl border border-border bg-card shadow-lg">
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-2xl font-bold text-foreground">
                      {stNightlyTotal > 0 ? `₹${stNightlyTotal}` : stFromPrice.length > 0 ? `₹${Math.min(...stFromPrice).toFixed(0)}` : "—"}
                    </span>
                    <span className="text-sm text-muted-foreground">/ night</span>
                    {stNightlyTotal > 0 && <span className="text-xs text-muted-foreground">(selected)</span>}
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="justify-start text-left font-normal">
                          <CalendarIcon size={16} className="mr-2" />{checkInDate ? format(checkInDate, "MMM d") : "Check-in"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={checkInDate} onSelect={setCheckInDate} disabled={(d) => d < new Date()} initialFocus />
                      </PopoverContent>
                    </Popover>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="justify-start text-left font-normal">
                          <CalendarIcon size={16} className="mr-2" />{checkOutDate ? format(checkOutDate, "MMM d") : "Check-out"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={checkOutDate} onSelect={setCheckOutDate} disabled={(d) => d < (checkInDate || new Date())} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {stGrandTotal > 0 && (
                    <div className="space-y-2 mb-6 pb-6 border-b border-border text-sm">
                      {Object.entries(stBedQty).filter(([, q]) => q > 0).map(([id, qty]) => {
                        const bed = p.vacant_beds.find(b => b.id === id);
                        if (!bed) return null;
                        return (
                          <div key={id} className="flex justify-between text-muted-foreground">
                            <span>{bed.bed_category} × {qty} × {nights}n</span>
                            <span>₹{parseFloat(bed.price_per_bed) * qty * nights}</span>
                          </div>
                        );
                      })}
                      {Object.entries(stRoomQty).filter(([, q]) => q > 0).map(([id, qty]) => {
                        const room = p.vacant_rooms.find(r => r.id === id);
                        if (!room) return null;
                        return (
                          <div key={id} className="flex justify-between text-muted-foreground">
                            <span>{room.room_category} × {qty} × {nights}n</span>
                            <span>₹{parseFloat(room.price_per_room) * qty * nights}</span>
                          </div>
                        );
                      })}
                      <div className="pt-2 border-t border-border space-y-1.5">
                        <div className="flex justify-between text-muted-foreground">
                          <span>Accommodation</span>
                          <span>₹{stGrandTotal}</span>
                        </div>
                        <div className="flex justify-between text-muted-foreground">
                          <span>Reserve (10%)</span>
                          <span>₹{stReserve}</span>
                        </div>
                        <div className="flex justify-between text-muted-foreground">
                          <span>GST on Reserve (18%)</span>
                          <span>₹{stGst}</span>
                        </div>
                        <div className="border-t border-border pt-1.5 space-y-1">
                          <div className="flex justify-between font-semibold text-foreground">
                            <span>Pay online now</span>
                            <span>₹{stPayNow}</span>
                          </div>
                          <div className="flex justify-between text-muted-foreground text-xs">
                            <span>Pay host on arrival</span>
                            <span>₹{stGrandTotal}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button className="w-full mb-4" size="lg" onClick={handleShortTermBookNow} disabled={stGrandTotal <= 0 || nights === 0 || stBooking}>
                    {stBooking ? "Booking..." : nights > 0 ? `Reserve · Pay ₹${stPayNow}` : "Select Dates"}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">Reserve amount charged now · accommodation paid on arrival</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <AnimatePresence>
          {isGalleryOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
              <Button variant="ghost" size="icon" className="absolute top-4 right-4 text-white hover:bg-white/20" onClick={() => setIsGalleryOpen(false)}><X size={24} /></Button>
              <Button variant="ghost" size="icon" className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20" onClick={() => setCurrentImageIndex(i => (i - 1 + images.length) % images.length)}><ChevronLeft size={32} /></Button>
              <div className="relative w-full max-w-5xl px-16">
                <motion.img key={currentImageIndex} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                  src={images[currentImageIndex]?.image || DEFAULT_PROPERTY_IMAGE} alt={`${p.name} ${currentImageIndex + 1}`}
                  className="w-full h-auto max-h-[80vh] object-contain rounded-lg" onError={(e) => { e.currentTarget.src = DEFAULT_PROPERTY_IMAGE; }} />
                <p className="text-center text-white mt-4">{currentImageIndex + 1} / {images.length}</p>
              </div>
              <Button variant="ghost" size="icon" className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20" onClick={() => setCurrentImageIndex(i => (i + 1) % images.length)}><ChevronRight size={32} /></Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
  // ── End short-term render ──────────────────────────────────────────────────

  if (!propertyData) return null;

  return (
    <div className="bg-background">
      {role ? <LandingHeader /> : <LandingPageHeader isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />}

      <main className="pt-20">
        <section className="relative">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-4">
              <Link to="/search" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ChevronLeft size={20} />
                <span>Back to search</span>
              </Link>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" className="gap-2">
                  <Share2 size={16} />
                  Share
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={`gap-2 ${isFavorite ? "text-red-500 border-red-500" : ""}`}
                  onClick={() => setIsFavorite(!isFavorite)}
                >
                  <Heart size={16} fill={isFavorite ? "currentColor" : "none"} />
                  Save
                </Button>
              </div>
            </div>

            {propertyData!.images && propertyData!.images.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-2 rounded-xl overflow-hidden md:h-[420px]">
                  <div
                    className="md:col-span-2 md:row-span-2 relative aspect-[4/3] overflow-hidden cursor-pointer group"
                    onClick={() => { setCurrentImageIndex(0); setIsGalleryOpen(true); }}
                  >
                    <img
                      src={propertyData!.images[0]?.image}
                      alt={propertyData!.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = DEFAULT_PROPERTY_IMAGE;
                      }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                  </div>
                  {propertyData!.images.slice(1, 5).map((image, index) => (
                    <div
                      key={index}
                      className="hidden md:block relative aspect-[4/3] overflow-hidden cursor-pointer group"
                      onClick={() => {
                        setCurrentImageIndex(index + 1);
                        setIsGalleryOpen(true);
                      }}
                    >
                      <img
                        src={image.image}
                        alt={`${propertyData!.name} ${index + 2}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = DEFAULT_PROPERTY_IMAGE;
                        }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      {index === 3 && propertyData!.images.length > 5 && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <div className="flex items-center gap-2 text-white">
                            <Camera size={20} />
                            <span className="font-medium">+{propertyData!.images.length - 5} more</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  className="mt-4 md:hidden"
                  onClick={() => { setCurrentImageIndex(0); setIsGalleryOpen(true); }}
                >
                  <Camera size={16} className="mr-2" />
                  View all {propertyData!.images.length} photos
                </Button>
              </>
            ) : (
              <div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-muted flex items-center justify-center">
                <img
                  src={DEFAULT_PROPERTY_IMAGE}
                  alt="No Image Available"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                  <Camera size={48} className="text-white/70" />
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <div>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                      {propertyData!.name}
                    </h1>
                    <div className="flex items-center gap-2 mt-2 text-muted-foreground">
                      <MapPin size={16} />
                      <span>{propertyData!.location.street_address}, {propertyData!.location.locality}, {propertyData!.location.city}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 bg-primary/10 px-3 py-1 rounded-full">
                    <Star size={18} className="fill-primary text-primary" />
                    <span className="font-semibold text-foreground">
                      {typeof propertyData!.average_rating === 'number' ? propertyData!.average_rating.toFixed(1) : 'N/A'}
                    </span>
                    <span className="text-muted-foreground">({propertyData!.total_reviews})</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 py-4 border-y border-border">
                  <div className="flex items-center gap-2">
                    <Bed size={20} className="text-muted-foreground" />
                    <span>{propertyData!.type}</span>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-foreground mb-4">About this place</h2>
                <ExpandableText text={propertyData!.description} className="text-muted-foreground leading-relaxed" />
              </div>

              <div>
                <h2 className="text-xl font-semibold text-foreground mb-4">Amenities</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {propertyData!.facilities?.map((amenity) => {
                    const detail = amenityDetails[amenity.toLowerCase()];
                    if (!detail) return null;
                    const Icon = detail.icon;
                    return (
                      <div key={amenity} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                        <Icon size={24} className="text-primary flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">{detail.label}</p>
                          <p className="text-sm text-muted-foreground">{detail.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {propertyData!.highlights && propertyData!.highlights.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-4">Highlights</h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {propertyData!.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 size={18} className="text-primary" />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {propertyData!.property_rules && propertyData!.property_rules.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-4">Property Rules</h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {propertyData!.property_rules.map((rule, index) => (
                      <li key={index} className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 size={18} className="text-primary" />
                        <span>{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {propertyData!.food_available && (
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-4">Food Availability</h2>
                  {propertyData!.food_availability_details && propertyData!.food_availability_details.length > 0 ? (
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {propertyData!.food_availability_details.map((detail, index) => (
                        <li key={index} className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle2 size={18} className="text-primary" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <UtensilsCrossed size={18} className="text-primary" />
                      <span>Food is available at this property (Breakfast, Lunch, Dinner).</span>
                    </div>
                  )}
                </div>
              )}

              {bookingType === "bed" && (
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-4">Select Beds by Sharing Type</h2>
                  <div className="space-y-3">
                    {Object.entries(
                      propertyData!.beds.reduce((acc, bed) => {
                        if (!acc[bed.bed_category!]) {
                          acc[bed.bed_category!] = [];
                        }
                        acc[bed.bed_category!].push(bed);
                        return acc;
                      }, {} as Record<string, Bed[]>)
                    ).map(([category, beds]) => {
                      const selectedQty = bedSelections[category]?.length || 0;
                      const availableBeds = beds.filter(b => !b.is_booked).length;
                      return (
                        <div
                          key={category}
                          className={`p-4 rounded-lg border transition-all ${
                            selectedQty > 0
                              ? "border-primary bg-primary/5"
                              : "border-border"
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="secondary" className="flex items-center gap-1">
                                  <Tag size={14} />
                                  {category}
                                </Badge>
                                {beds[0].co_roommate_gender && (
                                  <Badge variant="outline" className="flex items-center gap-1">
                                    <User size={14} />
                                    {beds[0].co_roommate_gender}
                                  </Badge>
                                )}
                              </div>
                              <p className="font-medium text-foreground text-lg">{beds[0].description}</p>
                              <div className="flex items-center gap-2 mt-2 text-muted-foreground text-sm">
                                {availableBeds !== undefined && (
                                  <span className="flex items-center gap-1">
                                    <Users size={16} />
                                    {availableBeds} beds available
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <CreditCard size={16} />
                                  <span className="text-primary font-bold">₹{beds[0].price_per_bed}</span> / night
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 rounded-full"
                                onClick={() => updateBedSelection(category, -1)}
                                disabled={selectedQty === 0}
                              >
                                <Minus size={14} />
                              </Button>
                              <span className="w-8 text-center font-medium">{selectedQty}</span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 rounded-full"
                                onClick={() => updateBedSelection(category, 1)}
                                disabled={selectedQty >= availableBeds}
                              >
                                <Plus size={14} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {getTotalBeds() > 0 && (
                    <div className="mt-4 p-3 bg-primary/10 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-foreground">
                          Total: {getTotalBeds()} bed{getTotalBeds() > 1 ? "s" : ""} selected
                        </span>
                        <span className="text-primary font-bold">₹{getTotalBedPrice()}/night</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {propertyData!.coordinates && (
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-4">Location</h2>
                  <div className="relative rounded-xl overflow-hidden bg-muted aspect-[16/9]">
                    <iframe
                      title="Property Location"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${propertyData!.coordinates.lng - 0.01}%2C${propertyData!.coordinates.lat - 0.01}%2C${propertyData!.coordinates.lng + 0.01}%2C${propertyData!.coordinates.lat + 0.01}&layer=mapnik&marker=${propertyData!.coordinates.lat}%2C${propertyData!.coordinates.lng}`}
                    />
                  </div>
                  <p className="mt-3 text-muted-foreground flex items-center gap-2">
                    <MapPin size={16} />
                    {propertyData!.location.street_address}
                  </p>
                </div>
              )}

              <div>
                <h2 className="text-xl font-semibold text-foreground mb-4">Reviews ({propertyData!.total_reviews})</h2>
                <div className="space-y-6">
                  {propertyData!.reviews.map((review, reviewIdx) => (
                    <motion.div
                      key={review.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="border-b border-border pb-6 last:border-0"
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-white font-bold text-lg"
                          style={{ backgroundColor: ['#6d28d9','#0d9488','#ea580c','#db2777','#2563eb','#16a34a'][reviewIdx % 6] }}
                        >
                          {(review.guest_first_name || 'G').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="font-medium text-foreground">{review.guest_first_name || 'Guest'}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  size={14}
                                  className={i < review.rating ? "fill-primary text-primary" : "text-muted-foreground"}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-muted-foreground">{review.comment}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <Button variant="outline" className="w-full mt-6">
                  View all {propertyData!.total_reviews} reviews
                </Button>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="p-6 rounded-xl border border-border bg-card shadow-lg">
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-2xl font-bold text-foreground">
                    {bookingType === "bed" && getTotalBeds() > 0
                      ? `₹${getTotalBedPrice()}`
                      : `₹${propertyData!.beds[0]?.price_per_bed || propertyData!.rooms[0]?.price_per_room}`
                    }
                  </span>
                  {bookingType === "bed" && getTotalBeds() > 0 && (
                    <span className="text-muted-foreground text-sm">({getTotalBeds()} bed{getTotalBeds() > 1 ? "s" : ""})</span>
                  )}
                  <span className="text-sm text-muted-foreground">/ night</span>
                </div>

                <div className="flex gap-2 mb-4">
                  <Button
                    variant={bookingType === "bed" ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => setBookingType("bed")}
                  >
                    <Bed size={16} className="mr-2" />
                    Beds
                  </Button>
                  <Button
                    variant={bookingType === "room" ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => setBookingType("room")}
                  >
                    <Home size={16} className="mr-2" />
                    Rooms
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="justify-start text-left font-normal">
                        <CalendarIcon size={16} className="mr-2" />
                        {checkInDate ? format(checkInDate, "MMM d") : "Check-in"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkInDate}
                        onSelect={setCheckInDate}
                        disabled={(date: Date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="justify-start text-left font-normal">
                        <CalendarIcon size={16} className="mr-2" />
                        {checkOutDate ? format(checkOutDate, "MMM d") : "Check-out"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkOutDate}
                        onSelect={setCheckOutDate}
                        disabled={(date: Date) => date < (checkInDate || new Date())}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-3 mb-4 p-3 border border-border rounded-lg">
                  <p className="text-xs text-muted-foreground">Guests</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Male</span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 rounded-full"
                        onClick={() => setMaleGuestsCount(Math.max(0, maleGuestsCount - 1))}
                      >
                        <Minus size={12} />
                      </Button>
                      <span className="w-6 text-center font-medium">{maleGuestsCount}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 rounded-full"
                        onClick={() => setMaleGuestsCount(maleGuestsCount + 1)}
                      >
                        <Plus size={12} />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Female</span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 rounded-full"
                        onClick={() => setFemaleGuestsCount(Math.max(0, femaleGuestsCount - 1))}
                      >
                        <Minus size={12} />
                      </Button>
                      <span className="w-6 text-center font-medium">{femaleGuestsCount}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 rounded-full"
                        onClick={() => setFemaleGuestsCount(femaleGuestsCount + 1)}
                      >
                        <Plus size={12} />
                      </Button>
                    </div>
                  </div>
                </div>

                {nights > 0 && (() => {
                  const ltBase = calculateTotal();
                  const ltReserve = Math.round(ltBase * PLATFORM_FEE_RATE);
                  const ltGst = Math.round(ltReserve * GST_RATE);
                  const ltPayNow = ltReserve + ltGst;
                  return (
                    <div className="space-y-1.5 mb-6 pb-6 border-b border-border text-sm">
                      <div className="flex justify-between text-muted-foreground">
                        <span>Accommodation</span>
                        <span>₹{ltBase}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Reserve (10%)</span>
                        <span>₹{ltReserve}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>GST on Reserve (18%)</span>
                        <span>₹{ltGst}</span>
                      </div>
                      <div className="border-t border-border pt-1.5 space-y-1">
                        <div className="flex justify-between font-semibold text-foreground">
                          <span>Pay online now</span>
                          <span>₹{ltPayNow}</span>
                        </div>
                        <div className="flex justify-between text-muted-foreground text-xs">
                          <span>Pay host on arrival</span>
                          <span>₹{ltBase}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <Button className="w-full mb-4" size="lg" onClick={handleBookNow} disabled={calculateTotal() <= 0}>
                  {nights > 0 ? `Reserve · Pay ₹${Math.round(calculateTotal() * PLATFORM_FEE_RATE * (1 + GST_RATE))}` : "Check Availability"}
                </Button>

                <p className="text-center text-xs text-muted-foreground mb-4">
                  Reserve amount charged now · accommodation paid on arrival
                </p>
              </div>
            </div>
          </div>
        </section>
        
      </main>

      <AnimatePresence>
        {isGalleryOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white hover:bg-white/20"
              onClick={() => setIsGalleryOpen(false)}
            >
              <X size={24} />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
              onClick={prevImage}
            >
              <ChevronLeft size={32} />
            </Button>

            <div className="relative w-full max-w-5xl px-16">
              <motion.img
                key={currentImageIndex}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                src={propertyData!.images[currentImageIndex]?.image || DEFAULT_PROPERTY_IMAGE}
                alt={`${propertyData!.name} ${currentImageIndex + 1}`}
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
                onError={(e) => {
                  e.currentTarget.src = DEFAULT_PROPERTY_IMAGE;
                }}
              />
              <p className="text-center text-white mt-4">
                {currentImageIndex + 1} / {propertyData!.images.length || 1}
              </p>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
              onClick={nextImage}
            >
              <ChevronRight size={32} />
            </Button>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {propertyData!.images.map((image, index) => (
                <button
                  key={index}
                  className={`w-16 h-12 rounded overflow-hidden border-2 transition-all ${
                    index === currentImageIndex ? "border-white" : "border-transparent opacity-50 hover:opacity-100"
                  }`}
                  onClick={() => setCurrentImageIndex(index)}
                >
                  <img
                    src={image.image}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = DEFAULT_PROPERTY_IMAGE;
                    }}
                  />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PropertyDetail;
